import { z } from 'zod';
import { dashboardLayoutSchema } from '../dashboard/dashboard-layout.schema';

/**
 * Berechtigungs-Katalog (ADR 0011, CLAUDE.md §11). Format `modul.aktion`.
 * Frontend und Backend teilen diese Konstanten; Rollen in der DB referenzieren sie.
 * `*` (siehe ALL_PERMISSIONS-Sonderfall) gewährt alle Rechte.
 */
export const PERMISSIONS = [
  'projects.read',
  'projects.create',
  'projects.update',
  'projects.viewfinancials', // Kaufmännischer Projekt-Tab (Controlling + Vorkalkulation) — nur GL/Sekretariat+
  'projects.viewordervalue', // Auftragswert lesen — unabhängig von projects.viewfinancials (das deckt es zusätzlich ab; die übrigen Finanz-/Kalkulationsfelder bleiben daran hängen)
  'crm.read',
  'crm.create',
  'crm.update',
  'inventory.read',
  'inventory.create',
  'inventory.update',
  'inventory.viewprices', // EK-/VK-Preise, Staffeln & Preishistorie sehen (ohne = „Artikelstamm-Light")
  'inventory.count', // Inventur anlegen/zählen (ADR 0058) — Abschließen/Buchen bleibt an inventory.update
  'inventory.uploadfile', // Zeichnungen/Dateien bei Artikeln hochladen — ZUSÄTZLICH zu inventory.update nötig (beide Rechte erforderlich)
  // Zusatzmaterial am Artikel pflegen (VB-26016, ADR 0088). Bewusst ein eigenes
  // Recht und NICHT `inventory.update`: Wer Preise und Bestände pflegt, legt
  // damit noch lange nicht fest, welche Schrauben technisch dazugehören — das
  // ist Technik-Arbeit. Lesen bleibt `inventory.read`.
  'inventory.kit',
  // Änderung am Artikelstamm BEANTRAGEN (VB-26016, ADR 0089). Die Technik darf
  // Artikel bearbeiten, wirksam wird es erst nach Freigabe durch das Sekretariat
  // (`engineering.approve`) — wer `inventory.update` hat, ändert weiterhin direkt.
  'inventory.requestchange',
  'engineering.read',
  'engineering.create',
  'engineering.update',
  'engineering.approve', // Vier-Augen-Freigabe von Artikelanträgen (Sekretariat, Phase C)
  'sales.read',
  'sales.create',
  'sales.update',
  'sales.release', // Vier-Augen-Freigabe von Verkaufsbelegen (ADR 0062)
  // Eine VERSENDETE Rechnung wieder in den Entwurf holen, um sie zu korrigieren
  // (Tech-Lead 2026-08-05). Bewusst ein eigenes Recht, nicht `sales.update`:
  // das Festschreiben ist die Regel, das Öffnen die begründete Ausnahme.
  'sales.reopeninvoice',
  'procurement.read',
  'procurement.create',
  'procurement.update',
  'forecast.read', // Bedarfsvorschau / Planung (ADR 0035)
  'forecast.create',
  'forecast.update',
  // Wareneingangsprüfung (ADR 0072) — bewusst wie commissioning.* geschnitten:
  // prüfen (Tablet, Lagerist) und buchen (Sekretariat) sind GETRENNTE Rechte.
  'goodsreceipt.read', // Wareneingänge sehen
  'goodsreceipt.check', // am Tablet prüfen und abschließen; schließt das Anlegen ein
  'goodsreceipt.book', // Lagerzugang buchen (Sekretariat) — getrennt vom Abschluss
  'commissioning.read', // Kommissionierung & Packliste (ADR 0036)
  'commissioning.create', // Auftrag anlegen (Sekretariat)
  'commissioning.release', // Freigabe (Projektleiter, Vier-Augen)
  'commissioning.pick', // Kommissionieren am Tablet (Lagerist) — schließt NICHT den Lagerausgang ein
  'commissioning.book', // Lagerausgang buchen (Admin/Sekretariat, ADR 0043 — getrennt vom Abschluss)
  'transport.read', // Transporte (ein-/ausgehend): Ladeplan, Verlade-Fotos (ADR 0066/0070)
  'transport.create', // Transport anlegen
  'transport.update', // Ladeplan pflegen (LKW/Container/Ladegut-Zuordnung), Fotos hochladen
  'transport.delete', // Transport/LKW/Container löschen (Soft-Delete)
  'transport.reopen', // Verladenen Transport wieder öffnen (Ladeplan entsperren, ADR 0066 §4)
  'blockedstock.read', // Sperrlager (Modul Logistik): Restmaterial ohne Stammdaten-Bezug
  'blockedstock.create',
  'blockedstock.update',
  'blockedstock.delete',
  'blockedstock.viewvaluation', // Bewertung (€/Stück) + Gesamtwert sehen — nur Admin/GL
  'production.read', // Fertigungsaufträge & Stücklisten (ADR 0008)
  'production.create',
  'production.update', // inkl. Freigabe (Material-Reservierung) und Fertigmeldung (Lagerbuchung)
  'assembly.read',
  'assembly.create',
  'assembly.update',
  'qm.read', // QM-Prozesse (8D/4D/KVP, ADR 0054)
  'qm.create',
  'qm.update',
  'analytics.read', // Auswertungen & finanzielle Kennzahlen (Analyse-Seiten, Marge, Fakturierungsgrad — ADR 0037)
  'selectionpool.manage', // Selektionspool: benannte, team-geteilte Auswahllisten sehen + bearbeiten (ADR 0047)
  'reporting.read', // Modul Reporting: Auftragseingang, Umsatz, Lagerwert, HF/NAL (Stakeholder + Buchhaltung)
  'dashboard.viewfinancials', // Finanz-Kacheln/Widgets im Start-Dashboard (Umsatz, Offene Posten, Auftragswerte) — dediziert, standardmäßig NUR Admin (ADR 0062)
  'export.download', // Globale Export-Sperre: CSV/Excel aus Listen + Reporting/Analyse als Datei herunterladen (nicht: normale Geschäftsdokument-PDFs wie Angebot/Rechnung/Lieferschein, die an der jeweiligen Modul-Berechtigung hängen)
  'accounting.read',
  'accounting.create',
  'accounting.update',
  'hr.read',
  'hr.create',
  'hr.update',
  'timetracking.read', // Zeiterfassung (Self-Service): eigene Einträge/Timer
  'timetracking.create',
  'timetracking.update',
  'timetracking.delete',
  'timetracking.manage', // Zeiterfassungs-Stammdaten (Firma/Kategorie/Projekt/Unterkat.)
  'leave.read', // Fehlzeiten/Urlaub (Self-Service): eigene Anträge
  'leave.create',
  'leave.update',
  'leave.delete',
  'leave.manage', // Anträge aller Mitarbeiter genehmigen/ablehnen + Urlaubsanalyse
  'travel.read', // Reisekosten (Self-Service): eigene Reisen
  'travel.create',
  'travel.update',
  'travel.delete',
  'travel.manage', // Reisekosten aller Mitarbeiter genehmigen/ablehnen + Monatsexport
  'settings.read', // Firmenstammdaten (Logo/Briefpapier/Bankdaten) — Admin-Einstellungsseite
  'settings.update',
  'legalentities.read', // Gesellschaften lesen (Basisrecht — jeder wählt eine Gesellschaft in Belegen/Projekten); Verwaltung bleibt zentral bei settings.update
  'dms.read', // Dokumentenmanagement / Dateiablage (ADR 0021)
  'dms.create',
  'dms.update',
  'dms.delete',
  'dms.approve', // Datei-/Versions-Freigaben (Vier-Augen, Phase 3)
  'admin.manage', // Benutzer & Rollen verwalten
  'search.read', // Modulübergreifende Suche nutzen (Basisrecht; Treffer werden je Modul-Recht gefiltert, ADR 0015)
  'chat.read', // Teamchat sehen/nutzen (Basisrecht; Sichtbarkeit je Kanal über Mitgliedschaft, ADR 0064)
  'chat.write', // Im Teamchat schreiben + Kanäle/DMs anlegen (Basisrecht)
  // VERALTET (ADR 0069): Der Suchindex ist mit dem Ausbau von Meilisearch entfallen,
  // dieses Recht gewährt nichts mehr. Es bleibt bewusst in der Liste: Rollen in der
  // Datenbank können es gespeichert haben, und die Rollen-UI schickt den gespeicherten
  // Stand beim Speichern zurück — ein Entfernen aus der Enum würde jede Bearbeitung
  // einer solchen Rolle mit 422 abweisen. Kann per Daten-Migration bereinigt werden.
  'search.reindex',
  'migration.run', // Altdaten-Import ausführen (ADR 0026) — hochsensibel
  'migration.read', // Import-Jobs/Berichte einsehen
  'data.read', // Daten-Editor: Tabellen/Zeilen ansehen
  'data.write', // Daten-Editor: Zeilen/Zellen ändern, anlegen, löschen — sensibel
  'audit.read', // Update-Log/Audit-Log einsehen (streng Admin — s. role-seeder.service.ts)
  // Verbesserungen/Bug-Meldungen (ADR 0078). Schnitt wie leave.*/travel.*:
  // read/create/update sind Self-Service-Basisrechte für JEDE Rolle (Melden muss
  // jeder können), der Datensatz-Filter im Repository erzwingt „nur eigene".
  'feedback.read', // eigene Meldungen sehen
  'feedback.create', // Meldung abgeben
  'feedback.update', // eigene Meldung nachbessern
  'feedback.manage', // alle Meldungen sehen, Status/Einordnung setzen, interne Notizen
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Rechte, die bewusst NICHT mehr in der Rollen-UI angeboten werden: Sie bleiben in
 * `PERMISSIONS` (gespeicherte Rollen sollen weiter validieren, siehe Kommentar dort),
 * gewähren aber nichts mehr und stehen daher in KEINER `PERMISSION_GROUPS`-Gruppe —
 * sonst böte die UI ein wirkungsloses Häkchen an. Die Gruppen-Invariante in
 * `permissions.spec.ts` nimmt genau diese Liste aus.
 */
export const DEPRECATED_PERMISSIONS: readonly Permission[] = ['search.reindex'];

/** Wildcard — gewährt jede Berechtigung (Admin-Rolle). */
export const PERMISSION_WILDCARD = '*';

/** Gruppierung für die Rollen-UI (Modul → Aktionen). */
export const PERMISSION_GROUPS: { module: string; label: string; permissions: Permission[] }[] = [
  { module: 'projects', label: 'Projekte', permissions: ['projects.read', 'projects.create', 'projects.update', 'projects.viewfinancials', 'projects.viewordervalue'] },
  { module: 'crm', label: 'CRM', permissions: ['crm.read', 'crm.create', 'crm.update'] },
  { module: 'inventory', label: 'Artikel & Lager', permissions: ['inventory.read', 'inventory.create', 'inventory.update', 'inventory.viewprices', 'inventory.count', 'inventory.uploadfile', 'inventory.kit', 'inventory.requestchange'] },
  {
    module: 'engineering',
    label: 'Engineering',
    permissions: ['engineering.read', 'engineering.create', 'engineering.update', 'engineering.approve'],
  },
  { module: 'sales', label: 'Verkauf', permissions: ['sales.read', 'sales.create', 'sales.update', 'sales.release', 'sales.reopeninvoice'] },
  { module: 'procurement', label: 'Beschaffung', permissions: ['procurement.read', 'procurement.create', 'procurement.update'] },
  { module: 'forecast', label: 'Bedarfsvorschau', permissions: ['forecast.read', 'forecast.create', 'forecast.update'] },
  { module: 'goodsreceipt', label: 'Wareneingang', permissions: ['goodsreceipt.read', 'goodsreceipt.check', 'goodsreceipt.book'] },
  { module: 'commissioning', label: 'Kommissionierung', permissions: ['commissioning.read', 'commissioning.create', 'commissioning.release', 'commissioning.pick', 'commissioning.book'] },
  { module: 'transport', label: 'Transporte', permissions: ['transport.read', 'transport.create', 'transport.update', 'transport.delete', 'transport.reopen'] },
  { module: 'blockedstock', label: 'Sperrlager', permissions: ['blockedstock.read', 'blockedstock.create', 'blockedstock.update', 'blockedstock.delete', 'blockedstock.viewvaluation'] },
  { module: 'production', label: 'Produktion', permissions: ['production.read', 'production.create', 'production.update'] },
  { module: 'assembly', label: 'Montage & Wartung', permissions: ['assembly.read', 'assembly.create', 'assembly.update'] },
  { module: 'qm', label: 'QM-Prozesse', permissions: ['qm.read', 'qm.create', 'qm.update'] },
  { module: 'analytics', label: 'Auswertungen & Kennzahlen', permissions: ['analytics.read', 'selectionpool.manage', 'dashboard.viewfinancials'] },
  { module: 'reporting', label: 'Reporting', permissions: ['reporting.read'] },
  // Global, modulübergreifend: sperrt CSV/Excel-Export aus Tabellen sowie Reporting-/
  // Analyse-Downloads. Geschäftsdokument-PDFs (Angebot, Rechnung, Lieferschein, Bestellung,
  // Packliste, Kassabuch …) bleiben davon unberührt — die hängen weiter nur an ihrer
  // Modul-Berechtigung, da sie zum normalen Arbeitsablauf gehören.
  { module: 'export', label: 'Datenexport', permissions: ['export.download'] },
  { module: 'accounting', label: 'Buchhaltung', permissions: ['accounting.read', 'accounting.create', 'accounting.update'] },
  { module: 'hr', label: 'HR', permissions: ['hr.read', 'hr.create', 'hr.update'] },
  {
    module: 'timetracking',
    label: 'Zeiterfassung',
    permissions: [
      'timetracking.read',
      'timetracking.create',
      'timetracking.update',
      'timetracking.delete',
      'timetracking.manage',
    ],
  },
  {
    module: 'leave',
    label: 'Urlaub & Fehlzeiten',
    permissions: ['leave.read', 'leave.create', 'leave.update', 'leave.delete', 'leave.manage'],
  },
  {
    module: 'travel',
    label: 'Reisekosten',
    permissions: ['travel.read', 'travel.create', 'travel.update', 'travel.delete', 'travel.manage'],
  },
  { module: 'settings', label: 'Einstellungen', permissions: ['settings.read', 'settings.update'] },
  // Eigene Gruppe (wie „Globale Suche"): Gesellschaften lesen ist eine Basisfunktion
  // für die Belegerfassung/Projektanlage — jede Rolle bekommt sie automatisch.
  // Anlegen/Ändern/Löschen bleibt zentral beim Admin (`settings.update`).
  { module: 'legalentities', label: 'Gesellschaften', permissions: ['legalentities.read'] },
  {
    module: 'dms',
    label: 'Dokumente',
    permissions: ['dms.read', 'dms.create', 'dms.update', 'dms.delete', 'dms.approve'],
  },
  // Eigene Gruppe (nicht unter „Administration"): Suche ist eine Basisfunktion,
  // die praktisch jede Rolle haben soll — sie soll beim Anlegen einer Rolle nicht
  // zwischen den sensiblen Admin-Rechten untergehen (§11, ADR 0038).
  { module: 'search', label: 'Globale Suche', permissions: ['search.read'] },
  // Eigene Gruppe (wie „Globale Suche"): Teamchat ist eine Basisfunktion, die
  // praktisch jede Rolle haben soll (ADR 0064).
  { module: 'chat', label: 'Teamchat', permissions: ['chat.read', 'chat.write'] },
  // Eigene Gruppe (wie „Teamchat"): Meldungen abgeben ist eine Basisfunktion für
  // jede Rolle; `feedback.manage` ist die Verwaltersicht darüber (ADR 0078).
  {
    module: 'feedback',
    label: 'Verbesserungen',
    permissions: ['feedback.read', 'feedback.create', 'feedback.update', 'feedback.manage'],
  },
  {
    module: 'admin',
    label: 'Administration',
    permissions: ['admin.manage', 'migration.run', 'migration.read', 'data.read', 'data.write', 'audit.read'],
  },
];

export const permissionSchema = z.enum(PERMISSIONS);

/** Antwort von GET /auth/me — wer bin ich und was darf ich. */
export const currentUserResponseSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  /** Effektive Berechtigungen (aufgelöst aus den Rollen; ['*'] = Admin). */
  permissions: z.array(z.string()),
  roles: z.array(z.string()),
  /** true = gültige Anmeldung (oder Dev-Session); false = Login nötig. */
  isAuthenticated: z.boolean(),
  /** true, wenn keine echte Authentifizierung aktiv ist (Auth nicht konfiguriert). */
  isDevSession: z.boolean(),
  /** true, wenn der Authenticator (TOTP) als zweiter Faktor eingerichtet ist (ADR 0034). */
  isTotpEnabled: z.boolean(),
  /**
   * Vom Nutzer selbst ausgeblendete Navigations-Gruppen (Anzeige-Präferenz,
   * KEINE Berechtigung) — Werte sind `NavGroup`-Namen aus apps/web/src/components/nav.ts.
   */
  hiddenNavGroups: z.array(z.string()),
  /**
   * Vom Nutzer selbst festgelegte Reihenfolge der Navigations-Gruppen
   * (Anzeige-Präferenz). Fehlende/unbekannte Gruppen werden vom Frontend in
   * der NAV_GROUPS-Standardreihenfolge ans Ende angehängt.
   */
  navGroupOrder: z.array(z.string()),
  /** Pro-Nutzer-Anordnung/-Sichtbarkeit der Dashboard-Widgets (ADR 0062). */
  dashboardLayout: dashboardLayoutSchema,
});
export type CurrentUserResponse = z.infer<typeof currentUserResponseSchema>;

/** Body für PATCH /auth/me/nav-preferences — ersetzt Ausblend-Liste und Reihenfolge komplett. */
export const updateNavPreferencesSchema = z.object({
  hiddenGroups: z.array(z.string().trim().min(1).max(60)).max(20),
  groupOrder: z.array(z.string().trim().min(1).max(60)).max(20),
});
export type UpdateNavPreferencesInput = z.infer<typeof updateNavPreferencesSchema>;
